import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { LegacyTablerIconSize } from '#pplx/frontend/aether/primitives/IconPrimitive/iconSizing.js';
import { IconPrimitive } from '#pplx/frontend/aether/primitives/IconPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { useAssetTypeDescription } from '#pplx/frontend/apps/ask/canvas-shared/useAssetInfo.js';
import { AttachmentRemoveButton } from '#pplx/frontend/apps/ask/composer-ui/attachments/AttachmentChip.js';
import { getAttachmentMediaKind } from '#pplx/frontend/apps/ask/composer-ui/attachments/attachmentMedia.js';
import { AttachmentThumbnail } from '#pplx/frontend/apps/ask/composer-ui/attachments/AttachmentThumbnail.js';
import { AttachmentTray, AttachmentTrayItem, } from '#pplx/frontend/apps/ask/composer-ui/AttachmentTray.js';
import { useAttachmentPreviewController } from '#pplx/frontend/apps/ask/composer-ui/useAttachmentPreviewController.js';
import Image from '#pplx/frontend/components/base/Image.js';
import TextButton from '#pplx/frontend/components/buttons/TextButton.js';
import { useDropZone } from '#pplx/frontend/components/hooks/useDropZone.js';
import { formatBytes, isFileImage, isFilePDF, isFileSpreadsheet, isFileText, truncateFilenameString, } from '#pplx/frontend/components/utils/fileUploadUtils.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import React, { useCallback, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
// Single-file event: defined here at its only call site rather than in a shared package.
const removeAttachmentClickedEvent = {
    event: 'remove attachment clicked',
    data: {},
};
const uploadedFilePreviewMessages = defineMessages({
    previewAttachment: {
        defaultMessage: 'Preview {fileName}',
        description: 'Accessible label for opening an attachment preview from the Ask input',
        id: '0a28b8629a',
    },
    removeAttachment: {
        defaultMessage: 'Remove {fileName}',
        description: 'Accessible label for removing an attachment from the Ask input',
        id: 'e10d78a30c',
    },
});
export const UploadedFilesList = React.memo(function UploadedFilesList({ uploadedFiles, presentation = 'default', renderTrailingAccessory, onRemoveFile, requireAttachment = false, isSubmitting = false, onFileSelect, onDrop, ref, }) {
    useIntl();
    const fileInputRef = useRef(null);
    const hasHadFilesRef = useRef(uploadedFiles.length > 0);
    if (uploadedFiles.length > 0) {
        hasHadFilesRef.current = true;
    }
    const { openAttachmentPreview, previewOverlay } = useAttachmentPreviewController();
    const handleDrop = useCallback((items) => {
        onDrop?.(items);
    }, [onDrop]);
    const { isDraggingFile, rootProps } = useDropZone({
        onDrop: handleDrop,
        disabled: !requireAttachment || uploadedFiles.length > 0,
        dropBehavior: 'file-viewport-wide',
    });
    const handleClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);
    const handleFileChange = useCallback((e) => {
        if (e.target.files && e.target.files.length > 0) {
            onFileSelect?.(e.target.files);
            e.target.value = '';
        }
    }, [onFileSelect]);
    if (uploadedFiles.length === 0) {
        if (!requireAttachment || hasHadFilesRef.current || isSubmitting)
            return null;
        return (_jsxs("div", { ref: ref, className: "px-2", ...rootProps, children: [_jsx("input", { ref: fileInputRef, type: "file", multiple: true, className: "hidden", onChange: handleFileChange }), _jsxs("div", { role: "button", tabIndex: 0, onClick: handleClick, onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            // Prevent default key behavior (Space scrolls, Enter may submit a form) — click is handled programmatically
                            e.preventDefault();
                            handleClick();
                        }
                    }, className: classNames('flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-8 transition-colors', isDraggingFile
                        ? 'border-super-strong text-super-primary'
                        : 'border-base text-secondary hover:border-focus'), children: [_jsx(IconPrimitive, { icon: $icon('folder-open'), size: 32 }), isDraggingFile ? (_jsx(TextPrimitive, { variant: "small", color: "super", children: _jsx(FormattedMessage, { defaultMessage: "Drop files here", description: "Label shown when dragging files over the upload drop zone", id: "938c448571" }) })) : (_jsx(TextPrimitive, { variant: "small", color: "light", children: _jsx(FormattedMessage, { defaultMessage: "Drag your file here or <clickUpload>click to upload</clickUpload>", description: "Instruction text in the file upload drop zone when no files are attached", id: "4eabcc64f3", values: {
                                    clickUpload: (chunks) => _jsx("span", { className: "text-super-primary", children: chunks }),
                                } }) }))] })] }));
    }
    const showMedia = presentation === 'media' &&
        uploadedFiles.every((file) => file.metadata?.kind !== 'remix_asset' &&
            getAttachmentMediaKind(file.file.name, file.file.type));
    return (_jsxs(_Fragment, { children: [_jsx(AttachmentTray, { ref: ref, layout: presentation === 'default' ? 'scroll' : 'wrap', children: uploadedFiles.map((uploadedFile, index) => (_jsx(UploadedFilePreview, { uploadedFile: uploadedFile, presentation: showMedia ? 'media' : presentation === 'default' ? 'default' : 'compact', trailingAccessory: renderTrailingAccessory?.(uploadedFile), onRemoveFile: onRemoveFile, onPreviewClick: openAttachmentPreview }, index))) }), previewOverlay] }));
});
const UploadedFilePreview = React.memo(function UploadedFilePreview({ uploadedFile, presentation, trailingAccessory, onRemoveFile, onPreviewClick, }) {
    const { track } = useInstrumentation();
    const compact = presentation !== 'default';
    const filenameLength = compact ? 24 : 30;
    const isLoading = uploadedFile.status === 'uploading';
    const intl = useIntl();
    const { $t } = intl;
    const remixMetadata = uploadedFile.metadata?.kind === 'remix_asset' ? uploadedFile.metadata : undefined;
    const remixAssetTypeLabel = useAssetTypeDescription(remixMetadata?.assetType);
    const fileTypeIcon = useMemo(() => {
        if (isFileImage(uploadedFile)) {
            return $icon('photo');
        }
        else if (isFilePDF(uploadedFile)) {
            return $icon('pdf');
        }
        else if (isFileSpreadsheet(uploadedFile)) {
            return $icon('file-spreadsheet');
        }
        else if (isFileText(uploadedFile)) {
            return $icon('file-text');
        }
        else {
            return $icon('file');
        }
    }, [uploadedFile]);
    const handleRemoveFile = useCallback(() => {
        track(removeAttachmentClickedEvent);
        onRemoveFile?.(uploadedFile.nextURL, uploadedFile.file_uuid ?? '', uploadedFile.id);
    }, [track, uploadedFile, onRemoveFile]);
    const handlePreviewClick = useCallback(() => {
        if (isLoading)
            return;
        onPreviewClick({ file: uploadedFile.file, kind: 'local' });
    }, [uploadedFile.file, isLoading, onPreviewClick]);
    const isClickable = !isLoading;
    const previewAriaLabel = $t(uploadedFilePreviewMessages.previewAttachment, {
        fileName: uploadedFile.file.name,
    });
    const removeAriaLabel = $t(uploadedFilePreviewMessages.removeAttachment, {
        fileName: uploadedFile.file.name,
    });
    const removeButton = presentation === 'default' ? (_jsx(TextButton, { ariaLabel: removeAriaLabel, icon: $icon('x'), size: "tiny", testId: "remove-uploaded-file", onClick: handleRemoveFile, variant: "common", pill: true, noPadding: true })) : (_jsx(AttachmentRemoveButton, { label: removeAriaLabel, media: presentation === 'media', onClick: handleRemoveFile }));
    const trailingAccessoryWithRemoveButton = (_jsxs(_Fragment, { children: [trailingAccessory, removeButton] }));
    if (remixMetadata) {
        return (_jsx(AttachmentTrayItem, { presentation: presentation, activationAriaLabel: previewAriaLabel, disabled: !isClickable, isMuted: isLoading, label: truncateFilenameString(remixMetadata.name, filenameLength), title: remixMetadata.name, description: remixAssetTypeLabel ||
                $t({
                    defaultMessage: 'App',
                    description: 'Fallback subtitle shown for remix attachments with unknown asset type',
                    id: 'e56872f5cc',
                }), leadingAccessory: remixMetadata.previewImageUrl && !isLoading ? (_jsx(Image, { alt: remixMetadata.name, src: remixMetadata.previewImageUrl, containerClassName: compact ? 'size-5 shrink-0' : 'size-8 shrink-0', imageClassName: "h-full w-full object-cover object-top", rounded: "md", includeLightBoxModal: false })) : (_jsx("div", { className: compact
                    ? 'flex size-full items-center justify-center'
                    : 'bg-soft dark:bg-subtle flex size-8 items-center justify-center rounded-lg', "data-testid": isLoading ? 'file-loading-icon' : 'file-type-icon', children: _jsx(IconPrimitive, { icon: isLoading ? $icon('loader-2') : $icon('layout-collage'), size: LegacyTablerIconSize.sm, className: classNames('text-tertiary', {
                        'animate-spin': isLoading,
                    }) }) })), onActivate: handlePreviewClick, trailingAccessory: trailingAccessoryWithRemoveButton }));
    }
    return (_jsx(AttachmentTrayItem, { presentation: presentation, activationAriaLabel: previewAriaLabel, disabled: !isClickable, isMuted: isLoading, label: truncateFilenameString(uploadedFile.file.name, filenameLength), title: uploadedFile.file.name, description: uploadedFile.file.size > 0 ? formatBytes(uploadedFile.file.size, intl) : undefined, leadingAccessory: _jsx("span", { className: compact ? 'contents' : 'block size-8 shrink-0 overflow-hidden rounded-md', children: _jsx(AttachmentThumbnail, { alt: uploadedFile.file.name, kind: isLoading
                    ? undefined
                    : getAttachmentMediaKind(uploadedFile.file.name, uploadedFile.file.type), file: compact && !isLoading ? uploadedFile.file : undefined, url: isLoading ? undefined : uploadedFile.thumbnailSource, fallback: _jsx("div", { className: compact
                        ? 'flex size-full items-center justify-center'
                        : 'bg-soft dark:bg-subtle flex size-8 items-center justify-center rounded-lg', "data-testid": isLoading ? 'file-loading-icon' : 'file-type-icon', children: _jsx(IconPrimitive, { icon: isLoading ? $icon('loader-2') : fileTypeIcon, size: LegacyTablerIconSize.sm, style: { color: '#64645F' }, className: isLoading ? 'animate-spin' : undefined }) }) }) }), onActivate: handlePreviewClick, trailingAccessory: trailingAccessoryWithRemoveButton }));
});
//# sourceMappingURL=UploadedFilesList.js.map